import type {
  SettingsSchemaAxeAccountJobFunctionValueEnum as JobFunctionSchema,
  SettingsSchemaAxeAccountLanguageValueEnum as LanguageSchema
} from '@deque/orgwide-settings-client';
import React, { useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getRealmInfo,
  updateAccount,
  updatePassword
} from '~/common/api-client';
import { useAuthContext } from '~/common/contexts/auth';
import { useConfiguration } from '~/common/contexts/Configuration';
import { useEnterprises } from '~/common/contexts/enterprises';
import { useFeatureFlagState } from '~/common/contexts/featureFlags';
import { useGlobalToast } from '~/common/contexts/globalToast';
import useMediaQuery from '~/common/hooks/useMediaQuery';
import validateGeneralSettingsForm, {
  type Errors
} from '~/common/utils/validate-general-settings-form';
import validatePasswordSettingsForm from '~/common/utils/validate-password-settings-form';
import Settings from '../components/AccountSettings/AccountSettings';

const AccountSettings = (): ReactElement => {
  const narrow = useMediaQuery('(max-width: 31.25rem)');
  const {
    user,
    loading: userLoading,
    error: userError,
    refreshUser,
    updateBillingUser
  } = useAuthContext();
  const {
    userSettings,
    updateUserSettings,
    loading: configurationLoading,
    loadingError
  } = useConfiguration();
  const { setContents } = useGlobalToast();
  const { t } = useTranslation();

  const { enterprises, isAdmin: isActiveEnterpriseAdmin } = useEnterprises();
  const restrictEmailFlagOn = useFeatureFlagState(
    'restrict_enterprise_email_changes_v1'
  );
  const shouldRestrictEmail =
    enterprises.length > 0 && restrictEmailFlagOn && !isActiveEnterpriseAdmin;

  const [generalErrors, setGeneralErrors] = useState<Errors>({});
  const [passwordErrors, setPasswordErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  /** input refs */
  const emailRef = useRef<HTMLInputElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const jobFunctionRef = useRef<HTMLSelectElement>(null);
  const languageRef = useRef<HTMLSelectElement>(null);
  const currentPasswordRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  const loading = configurationLoading || userLoading;
  const error = loadingError || userError;

  const handleGeneralSubmit = async (
    event: React.ChangeEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (
      !firstNameRef.current ||
      !lastNameRef.current ||
      (!shouldRestrictEmail && !emailRef.current)
    ) {
      setContents(t('Something went wrong. Please try again.'), 'caution');
      return;
    }

    const formErrors = validateGeneralSettingsForm(
      {
        firstName: firstNameRef.current,
        lastName: lastNameRef.current,
        ...(emailRef.current && { email: emailRef.current })
      },
      t
    );

    if (Object.keys(formErrors).length) {
      setGeneralErrors(formErrors);
      return;
    } else {
      setGeneralErrors({});
    }

    try {
      setSubmitting(true);

      await updateUserSettings({
        axeAccount: {
          jobFunction: {
            value:
              jobFunctionRef.current?.value !== ''
                ? (jobFunctionRef.current?.value as JobFunctionSchema)
                : null,
            type: 'default'
          },
          language: {
            value: languageRef.current?.value as LanguageSchema,
            type: 'default'
          }
        }
      });

      await updateAccount(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        user!.id,
        {
          ...(!shouldRestrictEmail && { email: emailRef.current?.value }),
          firstName: firstNameRef.current?.value,
          lastName: lastNameRef.current?.value
        },
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        user!.token
      );

      // The General tab reads names via `defaultValue` from the cached auth
      // user, so it must be refreshed before remount to avoid showing stale
      // values. Billing user is refreshed too because the header reads it.
      await Promise.all([refreshUser(), updateBillingUser()]);

      setContents(t('Settings successfully saved.'), 'confirmation');

      if (
        userSettings?.axeAccount.language.value !== languageRef.current?.value
      ) {
        window.location.reload();
      }
    } catch {
      setContents(t('Failed to save settings. Please try again.'), 'caution');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (
    event: React.ChangeEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    setSubmitting(true);

    if (
      !currentPasswordRef.current ||
      !passwordRef.current ||
      !confirmPasswordRef.current
    ) {
      setContents(t('Something went wrong. Please try again.'), 'caution');
      return;
    }

    let realmInfo;
    try {
      realmInfo = await getRealmInfo();
    } catch {
      setSubmitting(false);
      setContents(
        t('We were unable to fetch password requirements.'),
        'caution'
      );
      return;
    }

    const formErrors = validatePasswordSettingsForm(
      {
        currentPassword: currentPasswordRef.current,
        password: passwordRef.current,
        confirmPassword: confirmPasswordRef.current
      },
      realmInfo?.passwordPolicy || {},
      t
    );

    if (Object.keys(formErrors).length) {
      setPasswordErrors(formErrors);
      setSubmitting(false);
      return;
    } else {
      setPasswordErrors({});
    }

    try {
      await updatePassword(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        user!.id,
        {
          currentPassword: currentPasswordRef.current?.value || '',
          password: passwordRef.current?.value || '',
          confirmPassword: confirmPasswordRef.current?.value || ''
        },
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        user!.token
      );
      setContents(t('Password successfully changed.'), 'confirmation');
    } catch {
      setContents(t('Failed to save password. Please try again.'), 'caution');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Settings
      loading={loading}
      error={error}
      generalErrors={generalErrors}
      passwordErrors={passwordErrors}
      submitting={submitting}
      narrow={narrow}
      user={user}
      settings={userSettings}
      handleGeneralSubmit={handleGeneralSubmit}
      handlePasswordSubmit={handlePasswordSubmit}
      shouldRestrictEmail={shouldRestrictEmail}
      emailRef={emailRef}
      firstNameRef={firstNameRef}
      lastNameRef={lastNameRef}
      jobFunctionRef={jobFunctionRef}
      languageRef={languageRef}
      currentPasswordRef={currentPasswordRef}
      passwordRef={passwordRef}
      confirmPasswordRef={confirmPasswordRef}
    />
  );
};

export default AccountSettings;
